import React from "react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ProTip from "../../ProTip";
import { useReviewPageCheck } from "../context/reviewItemContext";
import { getTabState } from "../services/form.service";
import { useCoreApplicationConfigQuery } from "../../../api/core.api.slice";

const LINK_COLOR = "#31708f";
const titleButtonStyle = {
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  cursor: "pointer",
  color: "inherit",
};

const titleLinkStyle = {
  ...titleButtonStyle,
  color: LINK_COLOR,
  textDecoration: "underline",
};

function CustomTitle(props) {
  const {
    title = "",
    header = false,
    marginTop = false,
    showProTip = false,
    fieldKey = "",
    navigateFromReviewPage = [],
    handleTabChange = () => {},
    formTemplate = {},
    hideWhenDisabled = false,
    showSeparator = null,
    collapsible = false,
    expanded = true,
    onToggle,
  } = props;
  const { data: coreAppConfig } = useCoreApplicationConfigQuery();
  const isReviewContext = useReviewPageCheck();
  const applicationData = useSelector(
    (state) => state.formProps.applicationData
  );
  const { getValues } = useFormContext();
  const allowNavigation = navigateFromReviewPage.includes(
    applicationData?.status
  );
  const showHeaderAsLink =
    coreAppConfig?.reviewTabConfig?.showHeaderAsLink && allowNavigation;

  const indexTab = formTemplate?.components?.findIndex(
    (item) => item?.title === title
  );
  const isTabDisabled =
    typeof indexTab === "number" && indexTab > -1
      ? getTabState(formTemplate?.components[indexTab], getValues())
      : null;

  function onTitleClick(value) {
    if (allowNavigation) {
      const tabIndex = formTemplate?.components?.findIndex(
        (item) => item.title === value
      );
      const isDisabled = getTabState(
        formTemplate?.components[tabIndex],
        getValues()
      );
      if (!isDisabled) {
        handleTabChange(null, tabIndex, true);
        window.scrollTo({ top: 0 });
      }
    }
  }

  if (isTabDisabled && hideWhenDisabled) return null;

  const divider = (
    <Divider
      sx={[
        (theme) => ({
          borderBottomColor: theme.palette.primary.dark,
          borderBottomStyle: "solid",
          margin: "0.5rem 0",
          ...theme.applyStyles("light", {
            borderBottomColor: theme.palette.primary.light,
          }),
        }),
        header ? { borderBottomWidth: "2px" } : { borderBottomWidth: "1px" },
      ]}
    />
  );

  if (showSeparator) return divider;

  const titleContent = (
    <>
      {title}
      {showProTip &&
        (!applicationData.status ||
          applicationData.status === "Open" ||
          applicationData.status === "Rejected") &&
        !isReviewContext && <ProTip fieldKey={fieldKey} />}
    </>
  );

  const titleSx = [
    {
      paddingLeft: "0.625rem",
      fontWeight: 500,
    },
    header
      ? { fontSize: "1.25rem !important" }
      : { fontSize: "1.12rem !important" },
  ];

  const showAsLink = !isTabDisabled && showHeaderAsLink;

  const caret = collapsible ? (
    <ExpandMoreIcon
      aria-hidden
      sx={{
        marginRight: "1rem",
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s",
      }}
    />
  ) : null;

  const collapsibleButtonStyle = {
    ...titleButtonStyle,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    textAlign: "left",
  };

  let titleNode = titleContent;
  if (collapsible) {
    titleNode = (
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        style={collapsibleButtonStyle}
      >
        <span>{titleContent}</span>
        {caret}
      </button>
    );
  } else if (allowNavigation) {
    titleNode = (
      <button
        type="button"
        aria-expanded={!isTabDisabled}
        onClick={() => onTitleClick(title)}
        style={showAsLink ? titleLinkStyle : titleButtonStyle}
      >
        {titleContent}
      </button>
    );
  }

  return (
    <Box sx={[marginTop ? { mt: "1rem" } : { mt: undefined }]}>
      <Typography component="h2" sx={titleSx}>
        {titleNode}
      </Typography>
      {divider}
    </Box>
  );
}
CustomTitle.propTypes = {
  title: PropTypes.string,
  showSeparator: PropTypes.string,
  header: PropTypes.bool,
  marginTop: PropTypes.bool,
  showProTip: PropTypes.bool,
  hideWhenDisabled: PropTypes.bool,
  fieldKey: PropTypes.string,
  navigateFromReviewPage: PropTypes.oneOfType([PropTypes.array]),
  formTemplate: PropTypes.shape({}),
  handleTabChange: PropTypes.func,
  collapsible: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
};
CustomTitle.defaultProps = {
  title: "",
  showSeparator: null,
  header: false,
  marginTop: false,
  showProTip: false,
  hideWhenDisabled: false,
  fieldKey: "",
  navigateFromReviewPage: [],
  formTemplate: {},
  handleTabChange: () => {},
  collapsible: false,
  expanded: true,
  onToggle: undefined,
};

export default CustomTitle;
